"use client";

import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider, db } from "@/config/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      if (firebaseUser) {
        // Sync anonymous localStorage preferences to user profile in Firestore
        try {
          const localPrefsStr = localStorage.getItem("copleston_prefs");
          const userDocRef = doc(db, "usuarios", firebaseUser.uid);
          
          if (localPrefsStr) {
            const localPrefs = JSON.parse(localPrefsStr);
            // Sincronizar preferencias en firestore
            await setDoc(userDocRef, { preferencias_diseno: localPrefs }, { merge: true });
          } else {
            // Load user preferences from Firestore if they don't exist locally
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              const cloudData = userDocSnap.data();
              if (cloudData && cloudData.preferencias_diseno) {
                localStorage.setItem("copleston_prefs", JSON.stringify(cloudData.preferencias_diseno));
                
                // Apply root attributes
                const root = document.documentElement;
                const prefs = cloudData.preferencias_diseno;
                if (prefs.theme) root.setAttribute("data-theme", prefs.theme);
                if (prefs.font) root.setAttribute("data-font", prefs.font);
                if (prefs.layout) root.setAttribute("data-layout", prefs.layout);
                if (prefs.bionic) root.classList.add("bionic-mode");
                else root.classList.remove("bionic-mode");
                
                // Dispatch event to notify settings components
                window.dispatchEvent(new Event("copleston_settings_changed"));
              }
            }
          }
        } catch (e) {
          console.warn("Error syncing user preferences:", e);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error: any) {
      alert("Error Google Auth: " + error.message);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error: any) {
      alert("Error al cerrar sesión: " + error.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
